import userUtils from '@/pages/User/user.utils'
import { IUser } from '@/pages/Users/users.types'
import BadgeCount from '@/shared/Badges/BadgeCount'
import ToolTip from '@/shared/Common/Tooltip'
import cn from '@/utils/cn'
import timeUtils from '@/utils/time.utils'
import css from './reportTableContent.module.css'
import { TReportMaintenanceEvent } from '../OrganizationUsersReportView'
import Avatar from '@/shared/Avatar'
import { forwardRef, useMemo } from 'react'
import SkeletonContentRow from './SkeletonContentRow'
import { IMaintenanceEvent, TUnplannedMaintenanceEvent } from '@/pages/Calendar/calendar.types'
import reportTableUtils from '../../reportTable.utils'
import reportTableConfig from '../../reportTable.config'

interface IProps {
  events: {
    planned: IMaintenanceEvent[]
    unplanned: TUnplannedMaintenanceEvent[]
  }

  days: Date[]
  loading: boolean
  users: IUser[]
  hoverCol: number | null
  onHoverColChange: (v: number) => void
  onUserCellClick: (user: IUser) => void

  renderUsersCol?: boolean
  totalRowExist?: boolean
  headerRowExist?: boolean
}

const ReportTableContent = forwardRef<HTMLDivElement, IProps>(
  ({ totalRowExist = true, renderUsersCol = true, headerRowExist = true, ...props }: IProps, ref) => {
    const eventsByDate = useMemo((): Map<string, Record<IUser['id'], Record<TReportMaintenanceEvent, number>>> => {
      const map = new Map<string, Record<IUser['id'], Record<TReportMaintenanceEvent, number>>>()
      const mergedEvents = [
        ...props.events.planned,
        ...props.events.unplanned.map(reportTableUtils.parseUnplannedEvents),
      ]
      for (const event of mergedEvents) {
        const dateKey = timeUtils.formatDate(event.ts, 'yyyy-MM-dd')

        let byUser = map.get(dateKey)
        if (!byUser) {
          byUser = {}
          map.set(dateKey, byUser)
        }

        for (const userId of event.metadata.responsibleUsers) {
          if (!byUser[userId]) byUser[userId] = { [event.type]: 0 } as Record<TReportMaintenanceEvent, number>
          byUser[userId][event.type] += 1
        }
      }

      return map
    }, [props.events, props.users])

    const renderCell = (date: Date, user: IUser, colIndex: number, rowIndex: number) => {
      const dateKey = timeUtils.formatDate(date, 'yyyy-MM-dd')
      const byUser = eventsByDate.get(dateKey)
      const stats = byUser ? byUser[user.id] : null
      const total = stats ? Object.values(stats).reduce((acc, v) => acc + v, 0) : 0
      const tooltipPos = reportTableUtils.getTooltipPosition(
        colIndex,
        rowIndex,
        props.days.length,
        props.users.length,
        renderUsersCol
      )
      return (
        <div
          key={colIndex}
          onMouseOver={() => props.onHoverColChange(colIndex)}
          onMouseLeave={() => props.onHoverColChange(null)}
          data-hover={colIndex === props.hoverCol}
          data-user-cell-exist={Boolean(renderUsersCol)}
          className={css.cell}
          data-loading={props.loading}
        >
          {stats && total > 0 && (
            <ToolTip
              wrapperStyle={{
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
              text={
                <span className={tooltipPos === 'bottom' ? 'text-pre' : ''}>
                  {reportTableUtils.getTooltipText(stats)}
                </span>
              }
              position={tooltipPos}
            >
              <BadgeCount
                type={reportTableUtils.getBadgeType(stats)}
                style={{
                  height: '20px',
                  minWidth: '24px',
                }}
                count={total}
              />
            </ToolTip>
          )}
        </div>
      )
    }

    const renderRow = (user: IUser, rowIndex: number) => {
      return (
        <div
          key={rowIndex}
          className={cn(css.row, css.contentRow)}
          data-user-cell-exist={Boolean(renderUsersCol)}
          data-loading={props.loading}
        >
          {renderUsersCol && (
            <div className={css.userCell} onClick={() => props.onUserCellClick(user)} data-loading={props.loading}>
              <Avatar
                size="small"
                initials={userUtils.getUserAlias(user.userInfo)}
                userId={user.userInfo.avatar ? user.id : null}
                fileName={user.userInfo.avatar ? user.userInfo.avatar.fileName : null}
              />
              <span className="text-nm">{userUtils.getFullName(user.userInfo)}</span>
            </div>
          )}
          <div style={{ display: 'flex', flex: '1 1 0' }}>
            {props.days.map((date, i) => renderCell(date, user, i, rowIndex))}
          </div>
        </div>
      )
    }

    const contentRowsMaxHeightStyleValue = reportTableConfig.contentRowHeight(headerRowExist, totalRowExist)

    if (props.loading)
      return (
        <div ref={ref} className={css.contentRows} style={{ maxHeight: contentRowsMaxHeightStyleValue }}>
          {Array.from({ length: props.users ? props.users.length : 7 }).map((_, i) => (
            <SkeletonContentRow days={props.days} key={i} index={i} renderUsersCell={renderUsersCol} />
          ))}
        </div>
      )

    return (
      <div ref={ref} className={css.contentRows} style={{ maxHeight: `${contentRowsMaxHeightStyleValue}px` }}>
        {props.users.map(renderRow)}
      </div>
    )
  }
)

export default ReportTableContent
